import { useQueryClient } from '@tanstack/react-query';
import router from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import QnaReport from '@/components/atom/icon/report/QnaReport';
import UserBlock from '@/components/atom/icon/report/UserBlock';
import UserReport from '@/components/atom/icon/report/UserReport';
import UserUnBlock from '@/components/atom/icon/report/UserUnBlock';
import { SelectModalListItem } from '@/components/molecules/modal/select-modal';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';

import { useSetBlockingAccountMutation } from './qna/mobile/qna.gql.impl';
import useModal from './useModal';
import useToast from './useToast';

export const REPORT_TYPE = {
  QUESTION: 'question',
  ANSWER: 'answer',
  BOARD_POST: 'board_post',
  BOARD_POST_COMMENT: 'board_post_comment',
} as const;

export type REPORT_TYPE = (typeof REPORT_TYPE)[keyof typeof REPORT_TYPE];

export const ROLE_TYPE = {
  USER: 'user',
  DOCTOR: 'doctor',
  AI: 'AI',
} as const;

export type ROLE_TYPE = (typeof ROLE_TYPE)[keyof typeof ROLE_TYPE];

export const modalType = {
  setUserBlock: {
    title: (target: string) => `${target}를 차단할까요?`,
    body: (isUser: boolean) =>
      isUser ? '차단한 사용자의 글이 더 이상 보이지 않아요.' : '차단한 의료진의 답변을 볼 수 없어요.',
    grantText: '차단',
    toastMessage: (target: string) => `${target}를 차단했어요.`,
  },
  resetUserBlock: {
    title: (target: string, contentType: string) => `${target}의 ${contentType}을 다시 볼까요?`,
    body: (isUser: boolean) =>
      isUser ? '차단을 해제하면 사용자의 글을 볼 수 있어요.' : '차단을 해제하면 답변을 볼 수 있어요.',
    grantText: '차단 해제',
    toastMessage: '차단을 해제했어요.',
  },
} as const;

export interface ReportData {
  targetId: string | number;
  targetType: REPORT_TYPE;
  roleId: string;
  role: string;
  isBlocked: boolean;
}
const useReportModal = () => {
  const queryClient = useQueryClient();
  const [showReportModal, setShowReportModal] = useState<boolean>(false);
  const [reportData, setReportData] = useState<ReportData>();
  const [reportItems, setReportItems] = useState<Array<SelectModalListItem>>();

  const { showModal, closeModal } = useModal();
  const { showToast } = useToast();
  const isUser = reportData?.role === ROLE_TYPE.USER;
  const isQuestion = reportData?.targetType === REPORT_TYPE.QUESTION;
  const isBlocked = reportData?.isBlocked;
  const targetRoleName = useMemo(() => (isUser ? '사용자' : '의료진'), [isUser]);
  const contentType = useMemo(() => (isUser ? '글' : '답변'), [isUser]);

  const { mutate: blockMutate } = useSetBlockingAccountMutation({
    onError: () => {
      showToast({ message: '요청에 실패했습니다.' });
      onCloseReportModal();
    },
  });

  const onCloseReportModal = () => {
    setShowReportModal(false);
    setReportData(undefined);
    setReportItems([]);
  };

  const onShowReportModal = (data: ReportData) => {
    setReportData(data);
  };

  const onReportUser = () => {
    router.push(`/content/report?targetId=${reportData?.targetId}&type=${reportData?.targetType}&report_type=user`);
  };

  const onReportQna = () => {
    router.push(
      `/content/report?targetId=${reportData?.targetId}&type=${reportData?.targetType}&report_type=${reportData?.targetType}`
    );
  };

  const onBlockMutate = async (_isBlocked?: boolean) => {
    if (reportData) {
      await blockMutate(
        {
          blockingAccountMutationRequest: {
            blockTargetRoleType: reportData?.role,
            blockTargetId: reportData?.roleId,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries();
            showToast({
              message: _isBlocked
                ? modalType.resetUserBlock.toastMessage
                : modalType.setUserBlock.toastMessage(targetRoleName),
            });
            closeModal();
            onCloseReportModal();
          },
        }
      );
    }
  };

  const onBlockUser = () => {
    onCloseReportModal();
    showModal({
      type: MODAL_TYPE.ALERT,
      props: {
        title: isBlocked
          ? modalType.resetUserBlock.title(targetRoleName, contentType)
          : modalType.setUserBlock.title(targetRoleName),
        message: isBlocked ? modalType.resetUserBlock.body(isUser) : modalType.setUserBlock.body(isUser),
        buttons: [
          {
            label: '취소',
            onClick: () => closeModal(),
          },
          {
            type: isBlocked ? MODAL_BUTTON_TYPE.BOLD : MODAL_BUTTON_TYPE.DANGER,
            label: isBlocked ? '차단 해제' : '차단',
            onClick: () => onBlockMutate(isBlocked),
          },
        ],
      },
    });
  };

  const generateSelectList = () => {
    let selectItems: Array<SelectModalListItem> = [];

    if (reportData?.role !== ROLE_TYPE.AI) {
      selectItems = [
        {
          Icon: () => <UserReport />,
          text: isUser ? '사용자 신고' : '의료진 신고',
          onClick: onReportUser,
        },
        {
          Icon: () => (isBlocked ? <UserUnBlock /> : <UserBlock />),
          text: isBlocked ? '차단 해제하기' : isUser ? '사용자 차단' : '의료진 차단',
          onClick: onBlockUser,
        },
      ];
    }
    if (!isBlocked)
      selectItems = [
        {
          Icon: () => <QnaReport />,
          text: isQuestion ? '게시글 신고' : '답변 신고',
          onClick: onReportQna,
        },
        ...selectItems,
      ];

    setReportItems(selectItems);
  };

  useEffect(() => {
    if (reportItems?.length) {
      return setShowReportModal(true);
    }
    setShowReportModal(false);
  }, [reportItems]);

  useEffect(() => {
    if (reportData) {
      generateSelectList();
    }
  }, [reportData]);

  return {
    showReportModal,
    onShowReportModal,
    onCloseReportModal,
    reportData,
    reportItems,
  };
};

export default useReportModal;
